import api from './api';
import { isValidTokenFormat, isTokenExpired } from '../utils/tokenUtils';
import { API_CONFIG } from '../config/env';
import authkeyService from './authkeyService';

class AuthService {
  // User Authentication
  async userLogin(email, password) {
    try {
      const response = await api.request('/user/login/', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      
      if (response.success) {
        localStorage.setItem('authToken', response.data.access_token);
        localStorage.setItem('userData', JSON.stringify(response.data.user));
        return response;
      }
      throw new Error(response.error || 'Login failed');
    } catch (error) {
      throw error;
    }
  }

  // OTP Verification Methods
  async sendMobileOTP(mobile) {
    try {
      const response = await api.request('/user/send-mobile-otp/', {
        method: 'POST',
        body: JSON.stringify({ phone: mobile })
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async verifyMobileOTP(mobile, otp) {
    try {
      const response = await api.request('/user/verify-mobile-otp/', {
        method: 'POST',
        body: JSON.stringify({ phone: mobile, otp: otp })
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async sendEmailOTP(email, firstName = null) {
    try {
      // Generate 6-digit OTP
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      
      // Store OTP in localStorage with timestamp (10 minutes expiration)
      const otpData = {
        otp: otp,
        email: email,
        timestamp: Date.now(),
        expiresIn: 10 * 60 * 1000, // 10 minutes in milliseconds
      };
      localStorage.setItem(`email_otp_${email}`, JSON.stringify(otpData));
      
      // Send email via AuthKey API
      const name = firstName || email.split('@')[0];
      const result = await authkeyService.sendOTPEmail(email, otp, name);
      
      if (result.success) {
        return {
          success: true,
          message: 'OTP sent successfully',
        };
      } else {
        localStorage.removeItem(`email_otp_${email}`);
        throw new Error(result.error || 'Failed to send OTP email');
      }
    } catch (error) {
      throw error;
    }
  }

  async checkUserExists(email, phone) {
    try {
      const response = await api.request('/user/check-exists/', {
        method: 'POST',
        body: JSON.stringify({ email: email, phone: phone })
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async verifyEmailOTP(email, otp) {
    try {
      // Get stored OTP from localStorage
      const storedData = localStorage.getItem(`email_otp_${email}`);
      
      if (!storedData) {
        return {
          success: false,
          error: 'OTP expired or not found. Please request a new OTP.',
        };
      }
      
      const otpData = JSON.parse(storedData);
      
      // Check if OTP is expired
      const now = Date.now();
      const expiresAt = otpData.timestamp + otpData.expiresIn;
      
      if (now > expiresAt) {
        localStorage.removeItem(`email_otp_${email}`);
        return {
          success: false,
          error: 'OTP expired. Please request a new OTP.',
        };
      }
      
      // Verify OTP
      if (otpData.otp !== otp) {
        return {
          success: false,
          error: 'Invalid OTP. Please try again.',
        };
      }
      
      // OTP verified successfully - remove it and mark email as verified
      localStorage.removeItem(`email_otp_${email}`);
      
      // Store verification status (valid for 30 minutes)
      const verificationData = {
        email: email,
        verified: true,
        timestamp: Date.now(),
        expiresIn: 30 * 60 * 1000, // 30 minutes
      };
      localStorage.setItem(`email_verified_${email}`, JSON.stringify(verificationData));
      
      return {
        success: true,
        message: 'Email address verified successfully',
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to verify OTP',
      };
    }
  }

  // Forgot Password Methods
  async forgotPasswordCheckEmail(email) {
    try {
      const response = await api.request('/user/forgot-password/check-email/', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
      return response;
    } catch (error) {
      // Extract error message from response
      if (error.data && error.data.error) {
        return { success: false, error: error.data.error };
      }
      throw error;
    }
  }

  async forgotPasswordReset(email, newPassword, confirmPassword) {
    try {
      const response = await api.request('/user/forgot-password/reset/', {
        method: 'POST',
        body: JSON.stringify({
          email,
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });
      return response;
    } catch (error) {
      if (error.data && error.data.error) {
        return { success: false, error: error.data.error };
      }
      throw error;
    }
  }

  async userRegister(userData) {
    try {
      const response = await api.request('/user/register/', {
        method: 'POST',
        body: JSON.stringify(userData)
      });
      
      if (response.success) {
        localStorage.setItem('authToken', response.data.access_token);
        localStorage.setItem('userData', JSON.stringify(response.data.user));
        return response;
      }
      throw new Error(response.error || 'Registration failed');
    } catch (error) {
      // Extract specific error message from the error response
      let errorMessage = 'Registration failed';
      
      if (error.data) {
        // Handle validation errors from backend
        if (error.data.details) {
          // Handle field-specific validation errors
          const details = error.data.details;
          if (details.email && Array.isArray(details.email)) {
            errorMessage = details.email[0];
          } else if (details.phone && Array.isArray(details.phone)) {
            errorMessage = details.phone[0];
          } else if (details.first_name && Array.isArray(details.first_name)) {
            errorMessage = details.first_name[0];
          } else if (details.last_name && Array.isArray(details.last_name)) {
            errorMessage = details.last_name[0];
          } else if (details.password && Array.isArray(details.password)) {
            errorMessage = details.password[0];
          } else if (details.password_confirm && Array.isArray(details.password_confirm)) {
            errorMessage = details.password_confirm[0];
          } else {
            // Get the first available error message
            const firstError = Object.values(details)[0];
            if (Array.isArray(firstError)) {
              errorMessage = firstError[0];
            } else {
              errorMessage = firstError;
            }
          }
        } else if (error.data.error) {
          errorMessage = error.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  async userLogout() {
    try {
      await api.request('/user/logout/', {
        method: 'POST'
      });
    } catch (error) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
    }
  }

  // Admin Authentication
  async adminLogin(email, password) {
    try {
      const response = await api.adminRequest('/admin/login/', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      
      if (response.success) {
        localStorage.setItem('adminAuthToken', response.data.access_token);
        localStorage.setItem('adminRefreshToken', response.data.refresh_token);
        localStorage.setItem('adminAuth', JSON.stringify(response.data));
        localStorage.setItem('adminData', JSON.stringify(response.data.user));
        return response;
      }
      throw new Error(response.error || 'Admin login failed');
    } catch (error) {
      throw error;
    }
  }

  // Admin Token Refresh
  async adminRefreshToken() {
    try {
      const refreshToken = localStorage.getItem('adminRefreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/refresh-token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('adminAuthToken', data.data.access_token);
        return data.data.access_token;
      } else {
        throw new Error(data.error || 'Token refresh failed');
      }
    } catch (error) {
      // Clear invalid tokens
      localStorage.removeItem('adminAuthToken');
      localStorage.removeItem('adminRefreshToken');
      localStorage.removeItem('adminAuth');
      localStorage.removeItem('adminData');
      throw error;
    }
  }

  async adminLogout() {
    try {
      // Admin logout endpoint if available
      await api.adminRequest('/admin/logout/', {
        method: 'POST'
      });
    } catch (error) {
      // Continue to clear local data even if API call fails
    } finally {
      // CRITICAL: Clear ALL admin authentication data
      localStorage.removeItem('adminAuthToken');
      localStorage.removeItem('adminRefreshToken');
      localStorage.removeItem('adminAuth');
      localStorage.removeItem('adminData');
      // Also clear user tokens if present (defensive)
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
    }
  }

  // Get current user data
  getCurrentUser() {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  }

  getCurrentAdmin() {
    const adminData = localStorage.getItem('adminData');
    return adminData ? JSON.parse(adminData) : null;
  }

  // Check if user is authenticated (with token validation)
  isUserAuthenticated() {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    
    try {
      if (!isValidTokenFormat(token)) return false;
      if (isTokenExpired(token)) {
        // Try to refresh if refresh token exists
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          this.userLogout();
          return false;
        }
        // Token refresh will be handled by API calls
        return false; // Consider expired token as not authenticated
      }
      return true;
    } catch (error) {
      return !!token;
    }
  }

  isAdminAuthenticated() {
    const token = localStorage.getItem('adminAuthToken');
    if (!token) return false;
    
    try {
      if (!isValidTokenFormat(token)) return false;
      if (isTokenExpired(token)) {
        // Try to refresh if refresh token exists
        const refreshToken = localStorage.getItem('adminRefreshToken');
        if (!refreshToken) {
          this.adminLogout();
          return false;
        }
        // Token refresh will be handled by API calls
        return false; // Consider expired token as not authenticated
      }
      return true;
    } catch (error) {
      return !!token;
    }
  }

  // Clear all auth data
  clearAllAuth() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminData');
  }

  // Make authenticated admin API call with automatic token refresh
  async adminApiCall(url, options = {}) {
    try {
      let token = localStorage.getItem('adminAuthToken');
      
      if (!token) {
        throw new Error('No admin authentication token found');
      }

      // Make the API call with current token
      const response = await fetch(url, {
        ...options,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers
        }
      });

      if (response.status === 401) {
        try {
          const newToken = await this.adminRefreshToken();
          const retryResponse = await fetch(url, {
            ...options,
            headers: {
              'Authorization': `Bearer ${newToken}`,
              'Content-Type': 'application/json',
              ...options.headers
            }
          });
          return retryResponse;
        } catch (refreshError) {
          window.location.href = '/admin/login';
          throw refreshError;
        }
      }

      return response;
    } catch (error) {
      throw error;
    }
  }
}

export default new AuthService();
